import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api';
import LookMonitor from './LookMonitor';
import {
  VISUAL_MODE_INFO, backgroundChecks, backgroundOptions, backgroundPasses, ensureLookFonts, errorMessage, fontCss,
  cardOptions, isHex, kitColors, loadDocChannels, mainInk, panelOptions, readableFill, toHex,
} from './docShared';
import './documentaries.css';
import './docwizard.css'; // the studio's own styles: it also opens on the project page (going back from a finished video)

export const KIT_FIELDS = ['look', 'accent', 'background', 'card', 'panel', 'genre', 'visual_mode', 'notes', 'voice', 'voice_name', 'motion', 'avatar'];
export const MOTION_ORDER = ['tempo', 'reveal', 'entrance', 'highlight', 'treatment', 'surface'];
const MOTION_HELP = {
  tempo: 'How fast things move on screen.',
  reveal: 'How titles, captions and numbers write themselves on.',
  entrance: 'How a whole graphic arrives in the frame.',
  highlight: 'How a key word in a sentence is marked.',
  treatment: 'The texture over every graphic in the video.',
  surface: 'The cards and panels behind captions and quotes.',
};

const NICHE_HINTS = {
  cinematic: ['history', 'biograph', 'ancient', 'epic', 'story', 'mystery', 'documentary'],
  explainer: ['science', 'econom', 'explain', 'money', 'finance', 'business', 'education', 'psycholog', 'health'],
  editorial: ['geopolit', 'journal', 'essay', 'culture', 'travel', 'map', 'country', 'politic'],
  newsroom: ['crime', 'murder', 'investigat', 'conspir', 'scandal', 'fraud', 'case', 'police', 'spy', 'news'],
  tech: ['ai', 'artificial', 'space', 'robot', 'crypto', 'startup', 'software', 'future', 'tech', 'game'],
  organic: ['farm', 'food', 'cook', 'garden', 'nature', 'wellness', 'animal', 'craft', 'village'],
  noir: ['thriller', 'heist', 'mafia', 'cold case', 'noir', 'gangster', 'mystery'],
  retro: ['80s', '90s', 'retro', 'nostalgi', 'arcade', 'synth', 'pop culture', 'music'],
  gallery: ['luxury', 'fashion', 'art ', 'artist', 'design', 'architect', 'wealth', 'perfume', 'jewel'],
  playground: ['kids', 'children', 'child', 'toy', 'cartoon', 'fun fact', 'learning'],
  newsreel: ['world war', 'ww2', 'wwii', '1920', '1930', '1940', '1950', 'hollywood', 'vintage'],
  arena: ['sport', 'football', 'soccer', 'basketball', 'athlete', 'nba', 'nfl', 'boxing', 'fitness', 'racing'],
};

export function suggestLook(niche, looks) {
  const t = ` ${(niche || '').toLowerCase()} `;
  let best = looks?.cinematic ? 'cinematic' : Object.keys(looks || {})[0];
  let score = 0;
  Object.entries(NICHE_HINTS).forEach(([k, keys]) => {
    const s = keys.filter((x) => t.includes(x)).length;
    if (s > score && looks?.[k]) { best = k; score = s; }
  });
  return best;
}

function cleanMotion(m) {
  const out = {};
  MOTION_ORDER.forEach((ax) => { if (Array.isArray(m?.[ax]) && m[ax].length) out[ax] = [...m[ax]]; });
  return out;
}

export function cleanKit(kit) {
  const out = {};
  KIT_FIELDS.forEach((k) => {
    if (k === 'motion') {
      const m = cleanMotion(kit?.motion);
      if (Object.keys(m).length) out.motion = m;
    } else if (k === 'avatar') {
      if (kit?.avatar === true) out.avatar = true; // optional: only when switched on
    } else if (kit?.[k]) out[k] = String(kit[k]).trim();
  });
  return out;
}

/** Keep only the motion options the new look allows. */
function motionFor(motion, look) {
  const out = {};
  MOTION_ORDER.forEach((ax) => {
    const keep = (motion?.[ax] || []).filter((v) => look?.axes?.[ax]?.includes(v));
    if (keep.length) out[ax] = keep;
  });
  return out;
}

function MotionPanel({ look, motion, labels, onChange, onTry }) {
  return (
    <section className="bk-panel bk-motion">
      <h3 className="bk-h">Motion and texture</h3>
      <p className="bk-sub">Pick one or more of each, or let Claude vary it. Scenes rotate within your picks, so the
        channel keeps its feel without every scene looking the same. Click an option to see it in the preview.</p>
      <div className="bk-axes">
        {MOTION_ORDER.map((ax) => {
          const opts = look.axes?.[ax] || [];
          if (!opts.length) return null;
          const chosen = motion?.[ax] || [];
          const toggle = (v) => {
            const next = chosen.includes(v) ? chosen.filter((x) => x !== v) : [...chosen, v];
            onChange({ ...motion, [ax]: next });
            onTry(ax, v);
          };
          return (
            <div key={ax} className="bk-axis">
              <div className="bk-axis__head">
                <strong>{labels?.[ax]?.title || ax}</strong>
                <span>{MOTION_HELP[ax]}</span>
              </div>
              <div className="bk-axis__opts" role="group" aria-label={labels?.[ax]?.title || ax}>
                <button type="button" className={`bk-opt bk-opt--auto ${chosen.length ? '' : 'is-on'}`}
                        aria-pressed={!chosen.length} onClick={() => onChange({ ...motion, [ax]: [] })}>
                  Let Claude vary
                </button>
                {opts.map((v, i) => (
                  <button key={v} type="button" className={`bk-opt ${chosen.includes(v) ? 'is-on' : ''}`}
                          aria-pressed={chosen.includes(v)} onClick={() => toggle(v)}
                          title={i === 0 ? "This look's signature" : undefined}>
                    {labels?.[ax]?.options?.[v] || v}
                    {i === 0 && <span className="bk-opt__sig" aria-hidden="true" />}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export const sameKit = (a, b) => JSON.stringify(cleanKit(a)) === JSON.stringify(cleanKit(b));

/** Why this kit can't be used yet ('' when it can): a bad colour, or a background text would not read on. */
export function kitProblem(kit, look) {
  if (!look) return 'Choose a look.';
  if (kit.accent && !isHex(kit.accent)) return 'Use an accent colour like #E2B36E.';
  if (kit.background && !isHex(kit.background)) return 'Use a background colour like #15120F.';
  const names = { background: 'video background', card: 'text card colour', panel: 'text box colour' };
  for (const k of ['background', 'card', 'panel']) {
    if (kit[k] && !isHex(kit[k])) return `Use a ${names[k]} like #15120F.`;
    if (kit[k] && !backgroundPasses(kit[k], look, kit.accent)) {
      return `Text would not read on this ${names[k]}. Pick one of the suggested colours.`;
    }
  }
  return '';
}

/** One of the brand's three surfaces: suggested colours (all readable), a custom pick, and its readability checks. */
function SurfacePicker({ title, help, look, kit, colors, field, options, fallback, emptyLabel, sample, onPick }) {
  const value = isHex(kit[field]) ? toHex(kit[field]) : '';
  // an optional surface left on its default shows no checks (the look's own is handled by the engine); the
  // video background always has a colour, so it is always checked
  const current = value || (emptyLabel ? '' : fallback);
  const checks = current ? backgroundChecks(current, look, kit.accent) : [];
  const ok = checks.every((c) => c.ok);
  const [open, setOpen] = useState(false);
  const pick = (c) => onPick(c && toHex(c) !== fallback ? toHex(c) : '');
  return (
    <div className="bk-surface">
      <h4 className="bk-h4">{title}</h4>
      <p className="bk-sub">{help}</p>
      <div className="bk-accents">
        {emptyLabel && (
          <button type="button" className={`bk-accent bk-bg bk-bg--none ${!value ? 'is-on' : ''}`} onClick={() => onPick('')}
                  title={emptyLabel} aria-label={emptyLabel}>
            <span>∅</span>
          </button>
        )}
        {options.map((o) => (
          <button key={o} type="button" className={`bk-accent bk-bg ${current === o && (value || !emptyLabel) ? 'is-on' : ''}`}
                  style={{ background: o }} onClick={() => pick(o)} aria-label={`${title} ${o}`} title={o}>
            <span style={{ color: colors.accent }}>A</span>
          </button>
        ))}
        <label className="bk-accent bk-accent--custom" title="Custom colour">
          <input type="color" value={current || '#000000'} onChange={(e) => pick(e.target.value)} aria-label={`Custom ${title}`} />
          <span>+</span>
        </label>
        <input className="docs-input bk-hex" value={kit[field] || ''} placeholder={emptyLabel ? 'none' : fallback} maxLength={7}
               onChange={(e) => onPick(e.target.value.trim())} aria-label={`${title} hex`} />
      </div>
      {current && sample && (
        <div className="bk-sample" style={{ background: current }} aria-hidden="true">
          <span style={{ ...fontCss(look.fonts.label), color: checks[1]?.color, fontSize: 12 }}>{sample[0]}</span>
          <span style={{ ...fontCss(look.fonts.display), color: checks[0]?.color, fontSize: 22, textTransform: 'none' }}>
            {sample[1]}
          </span>
          <span style={{ ...fontCss(look.fonts.number), color: colors.accent, fontSize: 22 }}>{sample[2]}</span>
        </div>
      )}
      {current && (
        <button type="button" className={`bk-verdict ${ok ? '' : 'is-bad'}`} onClick={() => setOpen((v) => !v)}
                aria-expanded={open || !ok}>
          {ok ? '✓ Every text colour reads on it' : '✗ Some text would not read on it'}
          <span>{open || !ok ? 'hide details' : 'details'}</span>
        </button>
      )}
      {current && (open || !ok) && (
        <ul className={`bk-checks ${ok ? '' : 'is-bad'}`} aria-live="polite">
          {checks.map((c) => (
            <li key={c.role} className={c.ok ? 'is-ok' : 'is-bad'}>
              <span className="bk-checks__mark" aria-hidden="true">{c.ok ? '✓' : '✗'}</span>
              <span className="bk-checks__sw" style={{ background: c.on || current, color: c.color }}>Aa</span>
              <span className="bk-checks__label">{c.label}</span>
              <span className="bk-checks__ratio">{c.ratio.toFixed(1)} : 1 <em>needs {c.need}</em></span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LookItem({ name, look, active, suggested, onPick }) {
  const c = look.colors;
  return (
    <button type="button" className={`bk-look ${active ? 'is-on' : ''}`} onClick={() => onPick(name)}
            aria-pressed={active}>
      <span className="bk-look__name" style={{ ...fontCss(look.fonts.display), textTransform: 'none',
                                               letterSpacing: 0, lineHeight: 1.1 }}>
        {look.title}
      </span>
      <span className="bk-look__desc">{look.description}</span>
      <span className="bk-look__foot">
        <span className="bk-swatches" aria-hidden="true">
          {[look.background || c.panel, c.on_footage, c.accent, c.accent2].map((col, i) => (
            <span key={i} style={{ background: col }} />
          ))}
        </span>
        {suggested && <span className="bk-look__tag">Suggested for this channel</span>}
      </span>
    </button>
  );
}

/** Every colour of the kit at once (owner, 2026-10-23: "see all in one go — my background, the text background and
 *  everything"): six moments side by side, each drawn in your colours, each naming the colour it shows. Drawn in the
 *  page — it changes the instant a colour is picked. */
const SCENE_KEYS = ['title', 'caption', 'number', 'quote', 'card', 'plain']; // LookMonitor's moments, in order
const BOARD = [
  { scene: 5, place: 'brand', title: 'Plain screen', what: 'charts, maps, lists', surface: 'background' },
  { scene: 4, place: 'brand', title: 'Text card', what: 'a scene of words only', surface: 'card' },
  { scene: 2, place: 'brand', title: 'Big number', what: 'on your text card', surface: 'card' },
  { scene: 3, place: 'brand', title: 'Quote', what: 'on your text card', surface: 'card' },
  { scene: 1, place: 'picture', title: 'Name over a picture', what: 'in your text box', surface: 'panel' },
  { scene: 0, place: 'picture', title: 'Title over a picture', what: 'in your text box', surface: 'panel' },
];

function ColourBoard({ lookName, look, kit, colors, title, script, channelName, flash, onOpen }) {
  const chip = (surface) => {
    if (surface === 'background') return { hex: colors.background, name: 'Video background' };
    if (surface === 'card') return isHex(kit.card) ? { hex: colors.card, name: 'Text card' }
      : { hex: colors.card, name: 'Text card', note: 'same as background' };
    return colors.panel_chosen ? { hex: colors.panel, name: 'Text box' } : { hex: null, name: 'Text box', note: "none — the look's own" };
  };
  return (
    <div className="bk-board" aria-label="Your colours, every moment at once">
      {BOARD.map((b) => {
        const ch = chip(b.surface);
        return (
          <figure key={b.scene} className={`bk-board__tile ${flash === b.surface ? 'is-flash' : ''}`}>
            <button type="button" className="bk-board__shot" onClick={() => onOpen(b.scene)}
                    aria-label={`${b.title} — play it`}>
              <LookMonitor lookName={lookName} look={look} kit={kit} title={title} script={script}
                           channelName={channelName} compact autoplay={false} scene={b.scene} place={b.place} />
            </button>
            <figcaption>
              <strong>{b.title}</strong> <span className="bk-board__what">{b.what}</span>
              <span className="bk-board__chip">
                <i className={ch.hex ? '' : 'is-none'} style={ch.hex ? { background: ch.hex } : undefined} />
                {ch.name} {ch.note ? <em>{ch.note}</em> : <code>{ch.hex}</code>}
              </span>
            </figcaption>
          </figure>
        );
      })}
    </div>
  );
}

/**
 * Edit a channel's brand kit: look, accent, the ONE background (checked readable with every brand colour),
 * pictures, avatar, notes; motion and story type under "More options".
 * Controlled: `kit` / `onChange`. Saving goes to the channel when `channel` is given.
 */
export default function BrandKitEditor({ options, kit, onChange, channel, title, script, onSaved,
                                         showSave = true }) {
  const details = useMemo(() => options?.look_details || {}, [options]);
  const lookName = kit.look && details[kit.look] ? kit.look : null;
  const look = lookName ? details[lookName] : null;
  const colors = useMemo(() => kitColors(look, kit), [look, kit]);
  const suggested = useMemo(() => suggestLook(channel?.niche, details), [channel, details]);
  const [saving, setSaving] = useState(false);
  const [demo, setDemoState] = useState(null); // {axis, value, n}: replay the preview with this option
  const [cardPing, setCardPing] = useState(null); // a colour was picked: show the preview moment that uses it
  const [view, setView] = useState('board'); // every colour at once; "Watch it move" plays one moment
  const setDemo = (f) => { setView('motion'); setDemoState(f); }; // trying a motion option plays it
  const [flash, setFlash] = useState(null); // the colour just picked: its tiles light up for a moment
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(null), 1400);
    return () => clearTimeout(t);
  }, [flash]);
  const show = (scene) => {
    setCardPing((p) => ({ scene, n: (p?.n || 0) + 1 }));
    setFlash({ plain: 'background', card: 'card', caption: 'panel' }[scene] || null);
  };
  const [moreOpen, setMoreOpen] = useState(Boolean(kit.motion && Object.keys(kit.motion).length) || Boolean(kit.genre));
  const problem = kitProblem(kit, look);
  const pickedAccent = isHex(kit.accent) ? toHex(kit.accent) : (look ? toHex(look.colors.accent) : '#000000');
  const saved = channel?.brand_kit || {};
  const dirty = channel ? !sameKit(saved, kit) : false;

  useEffect(() => { ensureLookFonts(details); }, [details]);

  const set = (patch) => onChange({ ...kit, ...patch });
  const presets = useMemo(() => {
    if (!look) return [];
    const list = [look.colors.accent, look.colors.accent2, '#E2B36E', '#E3342F', '#3BE0FF', '#FFD23F', '#A7C957',
      '#F28C6B', '#C084FC'];
    return [...new Set(list.map((x) => x.toUpperCase()))].slice(0, 8);
  }, [look]);

  const save = () => {
    if (!channel || problem) return;
    setSaving(true);
    api.put(`/visual-engine/channels/${channel.id}/brand-kit`, cleanKit(kit))
      .then((r) => {
        toast.success(`Brand kit saved to ${channel.name}.`);
        loadDocChannels(true).catch(() => {});
        onSaved?.(r.data.channel);
      })
      .catch((e) => toast.error(errorMessage(e)))
      .finally(() => setSaving(false));
  };

  // a new accent: any chosen colour it no longer reads on goes back to the default (and says so)
  const [notice, setNotice] = useState('');
  const [mood, setMood] = useState('All');
  const setAccent = (a) => {
    const patch = { accent: a };
    const dropped = [];
    if (!a || isHex(a)) {
      [['background', 'video background'], ['card', 'text card colour'], ['panel', 'text box colour']].forEach(([k, n]) => {
        if (isHex(kit[k]) && !backgroundPasses(kit[k], look, a || undefined)) { patch[k] = ''; dropped.push(n); }
      });
    }
    setNotice(dropped.length ? `Your ${dropped.join(' and ')} did not read with this accent, so ${dropped.length > 1 ? 'they are' : 'it is'} back to the default.` : '');
    set(patch);
  };
  const pickTemplate = (t) => set({
    accent: t.accent.toUpperCase() === toHex(look.colors.accent) ? '' : t.accent.toUpperCase(),
    background: t.background.toUpperCase() === toHex(look.background || look.colors.panel) ? '' : t.background.toUpperCase(),
    card: t.card ? t.card.toUpperCase() : '',
    panel: t.panel ? t.panel.toUpperCase() : '',
  });
  const bgOptions = look ? backgroundOptions(look, kit.accent) : [];
  const cardOpts = look ? cardOptions(look, kit.accent, colors.background) : [];
  const panelOpts = look ? panelOptions(look, kit.accent) : [];

  return (
    <div className="bk bk--studio">
      <div className="bk-stage">
        {look ? (
          <>
            <div className="bk-view" role="tablist" aria-label="Preview">
              <button type="button" role="tab" aria-selected={view === 'board'} className={view === 'board' ? 'is-on' : ''}
                      onClick={() => setView('board')}>All colours at once</button>
              <button type="button" role="tab" aria-selected={view === 'motion'} className={view === 'motion' ? 'is-on' : ''}
                      onClick={() => setView('motion')}>Watch it move</button>
            </div>
            {view === 'board' ? (
              <ColourBoard lookName={lookName} look={look} kit={kit} colors={colors} title={title} script={script}
                           channelName={channel?.name} flash={flash}
                           onOpen={(i) => { setCardPing((p) => ({ scene: SCENE_KEYS[i], n: (p?.n || 0) + 1 })); setView('motion'); }} />
            ) : (
              <LookMonitor lookName={lookName} look={look} kit={kit} title={title} script={script}
                           channelName={channel?.name} demo={demo} axisLabels={moreOpen ? options?.motion_axes : null}
                           cardPing={cardPing} />
            )}
            <div className="bk-now" aria-label="Your colours">
              <span className="bk-now__look">{look.title}</span>
              <span className={`bk-now__ok ${problem ? 'is-bad' : ''}`}>{problem ? 'Text would not read' : 'All text readable'}</span>
              <ul className="bk-now__list">
                <li><i style={{ background: colors.background }} />Video background <code>{colors.background}</code></li>
                <li><i style={{ background: colors.card }} />Text cards {isHex(kit.card) ? <code>{colors.card}</code> : <em>same as background</em>}</li>
                <li><i style={{ background: colors.panel_chosen ? colors.panel : 'transparent' }} className={colors.panel_chosen ? '' : 'is-none'} />Text box {colors.panel_chosen ? <code>{colors.panel}</code> : <em>none — the look's own</em>}</li>
                <li><i style={{ background: colors.accent }} />Accent <code>{colors.accent}</code></li>
              </ul>
            </div>
          </>
        ) : (
          <div className="bk-monitor__empty">
            {Object.keys(details).length ? 'Choose a look to see it here.'
              : 'The engine did not send its looks. Update the engine (git pull), restart it, then restart the backend and reload this page.'}
          </div>
        )}
      </div>

      <div className="bk-side">
        <section className="bk-panel">
          <h3 className="bk-h">Look</h3>
          <div className="bk-looks" role="radiogroup" aria-label="Look">
            {Object.entries(details).map(([name, lk]) => (
              <LookItem key={name} name={name} look={lk} active={name === lookName} suggested={name === suggested}
                        onPick={(n) => set({ look: n, accent: kit.look === n ? kit.accent : '',
                                             background: kit.look === n ? kit.background : '',
                                           card: kit.look === n ? kit.card : '', panel: kit.look === n ? kit.panel : '',
                                             motion: motionFor(kit.motion, details[n]) })} />
            ))}
          </div>
        </section>

        {look && (look.palettes || []).length > 0 && (
          <section className="bk-panel">
            <h3 className="bk-h">Colour templates</h3>
            <p className="bk-sub">Ready-made colours for this look. Every one is checked: all text reads on it.</p>
            <div className="bk-genres bk-moods" role="group" aria-label="Mood">
              {['All', 'Dark', 'Light', 'Muted', 'Vivid'].map((m) => (
                <button key={m} type="button" className={`bk-genre ${mood === m ? 'is-on' : ''}`} aria-pressed={mood === m}
                        onClick={() => setMood(m)}>{m}</button>
              ))}
            </div>
            <div className="bk-templates">
              {look.palettes.filter((t) => mood === 'All' || t.mood === mood).map((t) => {
                const on = pickedAccent === toHex(t.accent) && colors.background === toHex(t.background)
                  && (kit.card || '') === (t.card || '').toUpperCase() && (kit.panel || '') === (t.panel || '').toUpperCase();
                const fill = readableFill(t.accent);
                return (
                  <button key={t.name} type="button" className={`bk-tpl ${on ? 'is-on' : ''}`} aria-pressed={on}
                          onClick={() => { pickTemplate(t); show('card'); }}>
                    <span className="bk-tpl__art" style={{ background: t.background }} aria-hidden="true">
                      <span className="bk-tpl__card" style={{ background: t.card }} />
                      <span style={{ ...fontCss(look.fonts.display), color: mainInk(t.background, look), textTransform: 'none' }}>Aa</span>
                      <span className="bk-tpl__bar" style={{ background: fill }} />
                    </span>
                    <span className="bk-tpl__name">{t.name}</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {look && (
          <section className="bk-panel bk-colors">
            <h3 className="bk-h">Your colours</h3>
            <h4 className="bk-h4 bk-h4--first">Accent</h4>
            <p className="bk-sub">Numbers, lines and highlights.</p>
            <div className="bk-accents">
              {presets.map((p) => (
                <button key={p} type="button" className={`bk-accent ${pickedAccent === p ? 'is-on' : ''}`}
                        style={{ background: p }} onClick={() => setAccent(p === look.colors.accent.toUpperCase() ? '' : p)}
                        aria-label={`Accent ${p}`} title={p} />
              ))}
              <label className="bk-accent bk-accent--custom" title="Custom colour">
                <input type="color" value={pickedAccent} onChange={(e) => setAccent(e.target.value.toUpperCase())}
                       aria-label="Custom accent colour" />
                <span>+</span>
              </label>
              <input className="docs-input bk-hex" value={kit.accent || ''} placeholder={look.colors.accent}
                     maxLength={7} onChange={(e) => setAccent(e.target.value.trim())} aria-label="Accent hex" />
            </div>
            {kit.accent && !isHex(kit.accent) && <p className="bk-warn">Use a colour like #E2B36E.</p>}
            {notice && <p className="bk-note">{notice}</p>}
            {isHex(kit.accent) && colors.accent !== toHex(kit.accent) && (
              <p className="bk-note">Words sit on the accent, so videos use the nearest shade they read on:{' '}
                <span className="bk-dot" style={{ background: colors.accent }} /> {colors.accent}</p>
            )}

            <SurfacePicker title="Video background" field="background" look={look} kit={kit} colors={colors}
                           help="The plain screen: what shows when there is no picture (charts, maps, lists)."
                           options={bgOptions} fallback={toHex(look.background || look.colors.panel)}
                           sample={['Farm size, in acres', 'Chart on the plain screen', '213 → 445']}
                           onPick={(v) => { set({ background: v }); show('plain'); }} />
            <SurfacePicker title="Text cards" field="card" look={look} kit={kit} colors={colors}
                           help="Scenes that are only words: a title, a quote, a big number."
                           options={cardOpts} fallback={colors.background} emptyLabel="Same as the video background"
                           sample={['Chapter two · 1929', 'The last ordinary day', '$26 billion']}
                           onPick={(v) => { set({ card: v }); show('card'); }} />
            <SurfacePicker title="Text over pictures" field="panel" look={look} kit={kit} colors={colors}
                           help={look.surface?.style === 'none'
                             ? 'The box behind captions and names on a picture. This look puts text straight on the picture until you pick one.'
                             : 'The box behind captions and names on a picture.'}
                           options={panelOpts} fallback={look.surface?.style === 'none' ? '' : toHex(look.colors.panel)}
                           emptyLabel={look.surface?.style === 'none' ? 'No box: text straight on the picture' : "The look's own box"}
                           sample={['Historian', 'Margaret Ellis', 'Columbia University']}
                           onPick={(v) => { set({ panel: v }); show('caption'); }} />
          </section>
        )}

        {look && (
          <section className="bk-panel bk-pictures">
            <h3 className="bk-h">Pictures</h3>
            <div className="bk-modes">
              {Object.keys(options?.visual_modes || VISUAL_MODE_INFO).map((m) => {
                const info = VISUAL_MODE_INFO[m] || { title: options.visual_modes[m], text: '' };
                const on = (kit.visual_mode || 'footage') === m;
                return (
                  <button key={m} type="button" className={`bk-mode ${on ? 'is-on' : ''}`} aria-pressed={on}
                          onClick={() => set({ visual_mode: m })}>
                    <span className={`bk-mode__art bk-mode__art--${m}`} aria-hidden="true" />
                    <strong>{info.title}</strong>
                    <span>{info.text}</span>
                  </button>
                );
              })}
            </div>
            <label className="bk-switch">
              <input type="checkbox" checked={kit.avatar === true}
                     onChange={(e) => set({ avatar: e.target.checked ? true : undefined })} />
              <span className="bk-switch__track" aria-hidden="true"><span className="bk-switch__dot" /></span>
              <span className="bk-switch__text">
                <strong>Talking avatar {kit.avatar === true ? 'on' : 'off'}</strong> — a DreamFace presenter speaks
                the narration where it adds impact.
              </span>
            </label>
          </section>
        )}

        {look && (
          <section className="bk-panel bk-notes">
            <h3 className="bk-h">Notes for Claude</h3>
            <textarea className="docs-textarea" rows={3} maxLength={1000} value={kit.notes || ''}
                      placeholder="For example: calm and archival, lots of maps, never use flashy transitions."
                      onChange={(e) => set({ notes: e.target.value })} />
          </section>
        )}

        {look && (
          <details className="bk-more" open={moreOpen} onToggle={(e) => setMoreOpen(e.currentTarget.open)}>
            <summary>More options <span>motion, texture and story type</span></summary>
            <MotionPanel look={look} motion={kit.motion || {}} labels={options?.motion_axes}
                         onChange={(m) => set({ motion: m })}
                         onTry={(axis, value) => setDemo((d) => ({ axis, value, n: (d?.n || 0) + 1 }))} />
            <section className="bk-panel">
              <h3 className="bk-h">Story type</h3>
              <div className="bk-genres">
                <button type="button" className={`bk-genre ${!kit.genre ? 'is-on' : ''}`} onClick={() => set({ genre: '' })}>
                  From the script
                </button>
                {Object.entries(options?.genres || {}).map(([k, t]) => (
                  <button key={k} type="button" className={`bk-genre ${kit.genre === k ? 'is-on' : ''}`}
                          onClick={() => set({ genre: k })}>{t}</button>
                ))}
              </div>
            </section>
          </details>
        )}
      </div>

      {showSave && channel && (
        <div className={`bk-savebar ${dirty ? 'is-dirty' : ''}`}>
          <span>
            {problem || (dirty ? `Changes not saved to ${channel.name} yet.` : `This is ${channel.name}'s saved brand kit.`)}
          </span>
          <button type="button" className="docs-btn docs-btn--primary" disabled={!dirty || saving || Boolean(problem)}
                  onClick={save}>
            {saving ? 'Saving…' : `Save to ${channel.name}`}
          </button>
        </div>
      )}
    </div>
  );
}
